import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, generatePath } from "react-router-dom";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, CircularProgress, Chip, Select, MenuItem, FormControl,
  InputLabel, TextField, Stack, Card, CardContent, useMediaQuery, useTheme,
} from "@mui/material";
import { getAdminUsers } from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import { useDebounce } from "../../../hooks/useDebounce";
import useListQueryState from "../../../hooks/useListQueryState";

const statusColor = { ACTIVE: "success", SUSPENDED: "warning", DEACTIVATED: "error" };

const UserList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, updateParams] = useListQueryState();
  const page = Number(searchParams.get("page")) || 0;
  const statusFilter = searchParams.get("accountStatus") || "";
  const [searchInput, setSearchInput] = useState(() => searchParams.get("search") || "");
  const search = useDebounce(searchInput, 300);
  const limit = 20;

  // Push the debounced search into the URL (and back to page 0), but only
  // once it actually diverges from what's there — otherwise this fires on
  // every mount (including a back-button remount) and wipes the page/filters
  // that were just restored from the URL.
  useEffect(() => {
    if (search === (searchParams.get("search") || "")) return;
    updateParams({ search, page: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", page, statusFilter, search],
    queryFn: () => getAdminUsers({ page: page + 1, limit, accountStatus: statusFilter || undefined, search: search || undefined }),
  });

  const handleRowClick = (userId) =>
    navigate(generatePath(frontendRoutes.ADMIN_USER_DETAIL, { userId }));

  if (isLoading) return <CircularProgress />;

  const users = data?.users || [];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>Users</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
        <TextField
          size="small"
          label="Search name / email / phone"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Account Status</InputLabel>
          <Select value={statusFilter} label="Account Status" onChange={(e) => updateParams({ accountStatus: e.target.value, page: undefined })}>
            <MenuItem value="">All</MenuItem>
            {["ACTIVE", "SUSPENDED", "DEACTIVATED"].map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {isMobile ? (
        <Stack spacing={2}>
          {users.map((u) => (
            <Card key={u._id} variant="outlined" onClick={() => handleRowClick(u._id)} sx={{ cursor: "pointer" }}>
              <CardContent>
                <Typography fontWeight={600}>{u.name}</Typography>
                <Typography variant="body2">{u.email || u.phoneNumber || "—"}</Typography>
                <Chip label={u.accountStatus || "ACTIVE"} color={statusColor[u.accountStatus || "ACTIVE"]} size="small" sx={{ mt: 0.5 }} />
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email / Phone</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Joined</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u._id} hover onClick={() => handleRowClick(u._id)} sx={{ cursor: "pointer" }}>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email || u.phoneNumber || "—"}</TableCell>
                <TableCell>{u.country || "—"}</TableCell>
                <TableCell>
                  <Chip label={u.accountStatus || "ACTIVE"} color={statusColor[u.accountStatus || "ACTIVE"]} size="small" />
                </TableCell>
                <TableCell>{new Date(u.createdAt).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={(_, p) => updateParams({ page: p || undefined })}
        rowsPerPage={limit}
        rowsPerPageOptions={[limit]}
      />
    </Box>
  );
};

export default UserList;
